import { apiFetch } from '../auth/apiFetch.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type CommandName =
  | 'move' | 'stop' | 'return_home' | 'start_mission' | 'pause' | 'resume'
  | 'start_capture' | 'stop_capture' | 'start_mapping' | 'stop_mapping';

export type CommandStatus =
  | 'pending' | 'sent' | 'acked' | 'completed' | 'failed' | 'cancelled';

export interface Command {
  id: number;
  robot_id: string;
  command: CommandName;
  params: Record<string, unknown>;
  status: CommandStatus;
  issued_by: string;
  issued_at: string;
  updated_at: string;
  result: Record<string, unknown> | null;
}

async function fetchRobotCommands(robotId: string): Promise<Command[]> {
  const r = await apiFetch(`/api/robots/${encodeURIComponent(robotId)}/commands`);
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { commands?: Command[] };
  return body.commands ?? [];
}

export function useRobotCommands(robotId: string | undefined) {
  return useQuery({
    queryKey: ['commands', robotId] as const,
    queryFn: () => fetchRobotCommands(robotId as string),
    enabled: !!robotId,
    // Slow polling fallback; useCommandStream pushes live via MQTT (D10) and
    // invalidates this query the instant a command is issued or transitions.
    // In background too: an operator typically watches the ROBOT with the
    // console unfocused, and a focus-gated fallback isn't a fallback.
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });
}

async function fetchPendingCommands(): Promise<Command[]> {
  const r = await apiFetch('/api/commands/pending');
  if (!r.ok) throw new Error(`status ${r.status}`);
  const body = (await r.json()) as { commands?: Command[] };
  return body.commands ?? [];
}

export function usePendingCommands() {
  return useQuery({
    queryKey: ['commands', 'pending'] as const,
    queryFn: fetchPendingCommands,
    refetchInterval: 3_000,
  });
}

export interface IssueCommandInput {
  robotId: string;
  command: CommandName;
  params?: Record<string, unknown>;
}

// C2 write path. Issues a command, then invalidates the affected queues.
export function useIssueCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ robotId, command, params }: IssueCommandInput): Promise<Command> => {
      const r = await apiFetch(`/api/robots/${encodeURIComponent(robotId)}/commands`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ command, params: params ?? {} }),
      });
      if (!r.ok) {
        const text = await r.text().catch(() => '');
        throw new Error(text || `status ${r.status}`);
      }
      return (await r.json()) as Command;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['commands', vars.robotId] });
      void qc.invalidateQueries({ queryKey: ['commands', 'pending'] });
    },
  });
}
